import { type CSSProperties } from 'react';
import {
  FreeLink as LibFreeLink,
  pickFreeLinkSettings
} from '@elfsight/widget-free-link';
import { useSettings } from '@wu/widget-core';
import { meta } from '../../meta';
import type { ButtonSettings } from '../../types';

export const FREE_LINK_OFFSET = 12;
const THREE_LINES_HEIGHT = 35;
const TWO_LINES_HEIGHT = 23; // height for forceTwoLines mode
const ONE_LINE_HEIGHT = 12;
const MAX_WIDTH = 80;

export function FreeLink() {
  const settings = useSettings();
  const { positionType, position, buttonLabel, buttonSecondLine } = settings;
  const freeLinkSettings = pickFreeLinkSettings(settings);

  const isFloating = positionType === 'floating';
  const iconOnly = !buttonLabel && !buttonSecondLine;
  const freeLinkHeight = getFreeLinkHeight(settings);

  const positionStyle = getPositionStyle(position, freeLinkHeight, iconOnly);

  return (
    <LibFreeLink
      {...freeLinkSettings}
      appAlias={meta.alias}
      appName={meta.name}
      mode="text"
      iconHidden
      textModeProps={{
        customColor: 'rgba(113, 113, 113, 0.8)',
        fontSize: 9,
        forceTwoLines: true
      }}
      style={
        isFloating
          ? {
              link: {
                ...positionStyle,
                position: 'absolute',
                width: 'max-content',
                zIndex: 999999999
              }
            }
          : {}
      }
    />
  );
}

export function getFreeLinkHeight(settings: ButtonSettings) {
  const { displayFreeLinkRemove = true } = pickFreeLinkSettings(settings);

  const isLimitedWidth = shouldLimitFreeLinkWidth(settings);

  const baseHeight = displayFreeLinkRemove ? TWO_LINES_HEIGHT : ONE_LINE_HEIGHT;
  const limitedByWidthHeight = displayFreeLinkRemove
    ? THREE_LINES_HEIGHT
    : TWO_LINES_HEIGHT;

  if (isLimitedWidth) {
    return limitedByWidthHeight;
  }

  return baseHeight;
}

function getPositionStyle(
  position: ButtonSettings['position'],
  freeLinkHeight: number,
  iconOnly: boolean
): CSSProperties {
  const {
    position: positionValue,
    horizontalOffset,
    verticalOffset
  } = position;

  const isLeftCorner =
    positionValue === 'bottom-left' || positionValue === 'top-left';
  const isRightCorner =
    positionValue === 'bottom-right' || positionValue === 'top-right';
  const isStickyOnSide = horizontalOffset === 0 && verticalOffset !== 0;

  const baseLeftPositionStyle: CSSProperties = {
    justifyContent: 'left',
    textAlign: 'left',
    top: '100%',
    left: '0',
    transform: 'none',
    ...(horizontalOffset < 4 ? { marginLeft: '4px' } : {}),
    ...(iconOnly ? { maxWidth: `${MAX_WIDTH}px` } : {})
  };

  const baseRightPositionStyle: CSSProperties = {
    justifyContent: 'right',
    textAlign: 'right',
    top: '100%',
    right: '0',
    left: 'auto',
    transform: 'none',
    ...(horizontalOffset < 4 ? { marginRight: '4px' } : {}),
    ...(iconOnly ? { maxWidth: `${MAX_WIDTH}px` } : {})
  };

  const leftRotatedPositionStyle: CSSProperties = {
    justifyContent: 'left',
    textAlign: 'left',
    transform: 'rotate(90deg)',
    transformOrigin: 'top left',
    top: 'auto',
    left: `-${FREE_LINK_OFFSET}px`,
    margin: horizontalOffset < 4 ? '4px 0' : '0',
    maxWidth: `${MAX_WIDTH}px`
  };

  const rightRotatedPositionStyle: CSSProperties = {
    justifyContent: 'right',
    textAlign: 'right',
    transform: 'rotate(90deg)',
    transformOrigin: 'bottom right',
    bottom: '0',
    right: `calc(100% + ${freeLinkHeight + FREE_LINK_OFFSET}px)`,
    left: 'auto',
    margin: horizontalOffset < 4 ? '4px 0' : '0',
    maxWidth: `${MAX_WIDTH}px`
  };

  if (isLeftCorner) {
    if (iconOnly) {
      return baseLeftPositionStyle;
    }

    if (positionValue === 'top-left' && isStickyOnSide) {
      return leftRotatedPositionStyle;
    }

    if (positionValue === 'bottom-left' && horizontalOffset === 0) {
      return leftRotatedPositionStyle;
    }

    return baseLeftPositionStyle;
  }

  if (isRightCorner) {
    if (iconOnly) {
      return baseRightPositionStyle;
    }

    if (positionValue === 'top-right' && isStickyOnSide) {
      return rightRotatedPositionStyle;
    }

    if (positionValue === 'bottom-right' && horizontalOffset === 0) {
      return rightRotatedPositionStyle;
    }

    return baseRightPositionStyle;
  }

  if (positionValue === 'left') {
    return iconOnly ? baseLeftPositionStyle : leftRotatedPositionStyle;
  }

  if (positionValue === 'right') {
    return iconOnly ? baseRightPositionStyle : rightRotatedPositionStyle;
  }

  // top or bottom
  return {
    top: '100%'
  };
}

function shouldLimitFreeLinkWidth(settings: ButtonSettings) {
  const { buttonLabel, buttonSecondLine, position } = settings;
  const { position: positionValue, horizontalOffset } = position;

  const iconOnly = !buttonLabel && !buttonSecondLine;
  const isRotatedButton =
    positionValue === 'left' ||
    positionValue === 'right' ||
    ((positionValue === 'bottom-left' ||
      positionValue === 'bottom-right' ||
      positionValue === 'top-left' ||
      positionValue === 'top-right') &&
      horizontalOffset === 0);

  return iconOnly || isRotatedButton;
}
